<template>
  <v-form
    lazy-validation
    v-model="valid"
    @submit.prevent="submit"
    ref="form"
    :disabled="localDriedAbaloneSummary.status === 'Completed'"
  >
      <v-container class="text-center">
        <h2>Pre-Cooked Weights</h2>
        <v-row>
          <v-col cols="6">
            <v-text-field
              filled
              data-cy="FormDriedAbaloneSummary-farm"
              :label="$$t('farm')"
              v-model="localDriedAbaloneSummary.farm"
            />
          </v-col>
          <v-col cols="6">
            <v-text-field
              filled
              data-cy="FormDriedAbaloneSummary-drySizeRange"
              :label="$$t('drySizeRange')"
              v-model="localDriedAbaloneSummary.drySizeRange"
            />
          </v-col>
          <v-col cols="6">
            <v-text-field
              filled
              data-cy="FormDriedAbaloneSummary-receivingDate"
              :label="$$t('receivingDate')"
              v-model="localDriedAbaloneSummary.receivingDate"
            />
          </v-col>
          <v-col cols="6">
            <v-text-field
              filled
              data-cy="FormDriedAbaloneSummary-cookingDate"
              :label="$$t('cookingDate')"
              v-model="localDriedAbaloneSummary.cookingDate"
            />
          </v-col>
          <v-col cols="6">
            <v-text-field
              filled
              data-cy="FormDriedAbaloneSummary-liveWeight"
              :label="$$t('liveWeight')"
              v-model="localDriedAbaloneSummary.liveWeight"
            />
          </v-col>
          <v-col cols="6">
            <v-text-field
              filled
              data-cy="FormDriedAbaloneSummary-outOfSalt"
              :label="$$t('outOfSalt')"
              v-model="localDriedAbaloneSummary.outOfSalt"
            />
          </v-col>
        </v-row>
        <v-simple-table>
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left"
                  v-for="(heading, index) in headings"
                  :key="`heading-${index}`">
                  {{ heading.text }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(item, lineIndex) in items"
                :key="item.id"
              >
                <td
                  v-for="(heading, index) in headings"
                  :key="`item-${lineIndex}-${index}`"
                  :data-cy="`line-${lineIndex}-col-${index}`">
                  <AdminOverrideField
                    v-if="heading.adminOverride"
                    v-model="item[heading.value]"
                    :data-cy="`line-${lineIndex}-col-${index}-input`"
                  />
                  <v-text-field
                    v-else
                    v-model="item[heading.value]"
                    @change="onLineChange(heading, $event)"
                    @input="onLineInput(heading, $event)"
                    :data-cy="`line-${lineIndex}-col-${index}-input`"
                  >
                  </v-text-field>
                </td>
              </tr>
              <tr>
                <td colspan="6">
                  <v-btn color="primary" @click="newItem">New Line</v-btn>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td
                  class="text-left"
                  v-for="(heading, index) in headings"
                  :key="heading.value">
                  <span
                    v-if="heading.hasTotal">
                    {{ localDriedAbaloneSummary[camelCase(`total ${heading.value}`)] }}
                  </span>
                  <v-text-field
                    v-if="heading.canEdit"
                    :data-cy="`FormDriedAbaloneSummary-footer-${index}`"
                    v-model="localDriedAbaloneSummary[camelCase(`total ${heading.value}`)]"
                    @change="onTotalChange(heading, $event)"
                    @input="onTotalInput(heading, $event)"
                  >
                  </v-text-field>
                </td>
              </tr>
              <tr class="mb-10">
                <td
                  class="text-left mb-10"
                  v-for="(heading, index) in headings"
                  :key="heading.value">
                    <v-btn
                      v-if="heading.hasHandover"
                      small
                      color="success"
                      class="ml-2 mt-5"
                      :data-cy="`FormDriedAbaloneSummary-handover-footer-${index}`"
                      @click="handover(`${heading.text}`)"
                    >
                      Handover
                    </v-btn>
                </td>
              </tr>
            </tfoot>
          </template>
        </v-simple-table>
        <div :class="{ dimmed: localDriedAbaloneSummary.status !== 'After Cooking' }">
          <SignVerification role="Process Controller" title="Completed By"
            v-model="localDriedAbaloneSummary.requestedBySignature" />
          <v-btn block color="success" @click="handover('Ready to be verified')">Handover</v-btn>
        </div>
        <hr class="mb-5 mt-5" />
        <div :class="{ dimmed: localDriedAbaloneSummary.status !== 'Ready to be verified' }">
          <SignVerification role="Production Manager" title="Verified By"
            v-model="localDriedAbaloneSummary.verifiedBySignature" />
          <v-btn block color="success" @click="handover('Completed')">Handover</v-btn>
        </div>
        <hr class="mb-5 mt-5" />
        <div class="d-flex justify-end mb-6">
          <v-btn color="default" outlined class="ml-4 btn-cancel" @click="cancel"
            data-cy="FormDriedAbaloneSummary-button-cancel">
            {{ $t('common.buttons.cancel') }}
          </v-btn>
          <v-btn color="primary" type="submit" class="ml-4" :loading="isSaving"
            data-cy="FormDriedAbaloneSummary-button-save">
              {{ $t('common.buttons.save') }}
            </v-btn>
        </div>
      </v-container>
  </v-form>
</template>

<script>
import loading from '@codehq/aurora-app-core/src/mixins/loading';
import { camelCase } from 'lodash';
import AdminOverrideField from './AdminOverrideField.vue';
import SignVerification from './SignVerification.vue';

export default {
  name: 'FormDriedAbaloneSummary',
  module: 'DriedAbaloneSummary',
  components: {
    AdminOverrideField,
    SignVerification,
  },
  mixins: [loading],
  props: {
    /**
    * Set the text box value. Default: undefined,
    */
    DriedAbaloneSummary: {
      type: Object,
      default: undefined,
    },
    SummaryItems: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      /**
      * Hold form line items
      */
      items: [{}],
      /**
      * Hold form validation state
      */
      valid: false,
      /**
      * Local value used for property localDriedAbaloneSummary
      */
      localDriedAbaloneSummary: {},
    };
  },
  computed: {
    /**
     * Returns the headings property
     */
    headings() {
      return [{
        text: 'Size Range',
        value: 'sizeRange',
      }, {
        text: 'Crates',
        value: 'crates',
        hasTotal: true,
      }, {
        text: 'After Soaking',
        value: 'afterSoaking',
        hasTotal: true,
        hasHandover: true,
      }, {
        text: 'Out of Brine',
        value: 'outOfBrine',
        hasTotal: false,
        calculated: true,
        canEdit: true,
        adminOverride: true,
        hasHandover: true,
      }, {
        text: 'After Scrubbing',
        value: 'afterScrubbing',
        hasTotal: false,
        canEdit: true,
        adminOverride: true,
      }, {
        text: 'After Cooking',
        value: 'afterCooking',
        hasTotal: false,
        canEdit: true,
        adminOverride: true,
      }];
    },
  },
  watch: {
    DriedAbaloneSummary: {
      immediate: true,
      deep: true,
      handler() {
        this.localDriedAbaloneSummary = this.DriedAbaloneSummary;
        console.debug('DriedAbaloneSummary', this.DriedAbaloneSummary);
        if (this.DriedAbaloneSummary?.items) {
          this.items = this.DriedAbaloneSummary.items;
        }
        this.$forceUpdate();
      },
    },
    SummaryItems: {
      immediate: true,
      deep: true,
      handler() {
        this.items = this.SummaryItems;
        this.$forceUpdate();
      },
    },
  },
  created() {
    this.localDriedAbaloneSummary = this.DriedAbaloneSummary;
    console.debug('DriedAbaloneSummary', this.DriedAbaloneSummary);
    if (this.DriedAbaloneSummary?.items) {
      this.items = this.DriedAbaloneSummary.items;
    }
  },
  methods: {
    calculateAfterCooking(value) {
      this.items = this.items.map((item) => {
        const i = { ...item };
        i.afterCooking = Math.round(value / item.afterSoaking);
        return i;
      });
    },
    calculateAfterScrubbing(value) {
      this.items = this.items.map((item) => {
        const i = { ...item };
        i.afterScrubbing = Math.round(value / item.afterSoaking);
        return i;
      });
    },
    calculateOutOfBrine(value) {
      this.items = this.items.map((item) => {
        const i = { ...item };
        const outOfBrine = Math.round(value / item.afterSoaking);
        i.outOfBrine = outOfBrine;
        return i;
      });
    },
    calculateSizeRange(value) {
      const drySizeRange = this.localDriedAbaloneSummary.drySizeRange?.split('-') || [1000, 0];
      const sizes = value.split('-').map((s) => parseInt(s, 10));
      const min = Math.min(drySizeRange[0], sizes[0]);
      const max = Math.max(drySizeRange[1], sizes[1]);
      this.localDriedAbaloneSummary.drySizeRange = `${min}-${max}`;
      this.$forceUpdate();
    },
    /**
     * Triggered when a line is changed
     */
    onLineChange(item, value) {
      // eslint-disable-next-line default-case
      switch (item.value) {
        case 'sizeRange':
          this.calculateSizeRange(value);
          break;
      }
    },
    /**
     * Triggered when a line input is changed
     */
    onLineInput(item) {
      console.debug(item.value);
      // eslint-disable-next-line default-case
      switch (item.value) {
        case 'crates':
          this.localDriedAbaloneSummary.totalCrates = this.total('crates');
          break;
        case 'afterSoaking':
          this.localDriedAbaloneSummary.totalAfterSoaking = this.total('afterSoaking');
          break;
      }
    },
    /**
     * Triggered when a total line is changed
     */
    onTotalChange() {
    },
    /**
     * Triggered when a total line input is changed
     */
    onTotalInput(item, value) {
      console.debug({ item, value });
      // eslint-disable-next-line default-case
      switch (item.value) {
        case 'outOfBrine':
          this.calculateOutOfBrine(value);
          break;
        case 'afterScrubbing':
          this.calculateAfterScrubbing(value);
          break;
        case 'afterCooking':
          this.calculateAfterCooking(value);
          break;
      }
    },
    handover(status) {
      this.localDriedAbaloneSummary.status = status;
      const { localDriedAbaloneSummary, items } = this;
      this.$emit('save', {
        localDriedAbaloneSummary,
        items,
      });
    },
    /**
     * Executes newItem
     */
    newItem() {
      this.items.push({});
    },
    /** Submit Form data trigger
     * @event save
     * @type {object}
     */
    submit() {
      // validate
      if (this.$refs.form.validate()) {
        const { localDriedAbaloneSummary, items } = this;
        this.$emit('save', { localDriedAbaloneSummary, items });
        this.isSaving = true;
      }
    },
    /**
     * Returns the string as a capitalized string
     * @param {string} column
     */
    camelCase,
    /** Cancel form trigger
     * @event cancel
     */
    cancel() {
      this.$emit('cancel');
    },
    /**
     * Returns the total of a column
     * @param {string} column
     */
    total(column) {
      return this.items.reduce((acc, item) => acc + (parseInt(item[column], 10) || 0), 0);
    },
  },
};
</script>

<docs>
# FormDriedAbaloneSummary example

'''js
<FormDriedAbaloneSummary :DriedAbaloneSummary="DriedAbaloneSummary" />
'''
</docs>
